import { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AHUControlModal } from './ahu-control-modal';
import { StatusBadge } from './status-badge';
import { ModeBadge } from './mode-badge';
import { FanSpeedBadge } from './fan-speed-badge';
import { ProgressBar } from './progress-bar';
import { ValueBadge } from './value-badge';
import { ConnectionBadge } from './connection-badge';
import { PMVBadge } from './pmv-badge';
// Define types to avoid import errors
type SelectedDeviceId = string;
import arrowRight from '@/assets/arrowRight.svg'
import { MultiAHUControlModal } from './multi-control-modal';
import { AutopilotState } from './autopilot-state';
import { OpMode, AutoPilotStatus } from '@/types';

type Status = 'on' | 'off' | 'alarm';
type Mode = 'cool' | 'fan' | 'dry';
type FanSpeed = 'auto' | 'low' | 'med' | 'high';

interface AHUTable {
  tableData: AHUData[]
  isLoading: boolean
  onRefresh?: () => void
}

export interface AHUData {
  id: string;
  autopilotId: string,
  status: Status;
  isAlarm: boolean
  isOnline: boolean;
  autopilotStatus: AutoPilotStatus;
  fanSpeed: FanSpeed | undefined;
  deviceName: string;
  event?: string;
  working: number;
  avgTemp: number | null;
  avgRH: number | null;
  pmv: number | null;
  setTemp: number | null;
  returnTemp: number | null;
  mode: Mode | null;
  opMode: OpMode;
  power: number;
  consumption: number;
  hours: number;
  user?: string;
  lastUpdated?: string;
}


const formatNumber = (num: number) => {
  // Round to integer and format with comma separators
  return Math.round(num).toLocaleString();
};

// Define column structure for better maintainability
interface TableColumn {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (data: AHUData) => React.ReactNode;
}

// Define columns with consistent properties
const columns: TableColumn[] = [
  {
    key: 'checkbox',
    header: '',
    width: '55px',
    align: 'center',
  },
  {
    key: 'deviceName',
    header: 'Device Name',
    width: '130px',
    align: 'left',
    render: (ahu) => (
      <span className={cn(
        "heading-7 text-primary",
        ahu.status === 'off' && "text-[#788796]",
        ahu.isAlarm && "text-[#EF4337]",
      )}>
        {ahu.deviceName}
      </span>
    )
  },
  {
    key: 'status',
    header: 'Status',
    width: '80px',
    align: 'left',
    render: (ahu) => <StatusBadge status={ahu.status} isAlarm={ahu.isAlarm} />
  },
  {
    key: 'connection',
    header: 'Connection',
    width: '100px',
    align: 'left',
    render: (ahu) => <ConnectionBadge isOnline={ahu.isOnline} />
  },
  {
    key: 'autopilot',
    header: 'Autopilot',
    width: '80px',
    align: 'center',
    render: (ahu) => <AutopilotState status={ahu.autopilotStatus} isAlarm={ahu.isAlarm} />
  },
  {
    key: 'working',
    header: 'Working %',
    width: '130px',
    align: 'center',
    render: (ahu) => <ProgressBar value={ahu.working} />
  },
  {
    key: 'avgTemp',
    header: 'Room Temp',
    width: '100px',
    align: 'center',
    render: (ahu) => <ValueBadge value={ahu.avgTemp} unit="°C" status={ahu.status} isAlarm={ahu.isAlarm} type="temperature" />
  },
  {
    key: 'avgRH',
    header: 'Humidity',
    width: '100px',
    align: 'center',
    render: (ahu) => <ValueBadge value={ahu.avgRH} unit="%" status={ahu.status} isAlarm={ahu.isAlarm} type="humidity" />
  },
  {
    key: 'pmv',
    header: 'PMV',
    width: '80px',
    align: 'center',
    render: (ahu) => <PMVBadge value={ahu.pmv} status={ahu.status} isAlarm={ahu.isAlarm} />
  },
  {
    key: 'setTemp',
    header: 'Setpoint',
    width: '80px',
    align: 'center',
    render: (ahu) => ahu.setTemp !== null && ahu.setTemp !== undefined ? ahu.setTemp.toFixed(1) : '-'
  },
  {
    key: 'returnTemp',
    header: 'Return Temp',
    width: '80px',
    align: 'center',
    render: (ahu) => ahu.returnTemp !== null && ahu.returnTemp !== undefined ? ahu.returnTemp.toFixed(1) : '-'
  },
  {
    key: 'mode',
    header: 'Mode',
    width: '70px',
    align: 'center',
    render: (ahu) => <ModeBadge mode={ahu.mode} />
  },
  {
    key: 'fanSpeed',
    header: 'Fan Speed',
    width: '70px',
    align: 'center',
    render: (ahu) => <FanSpeedBadge fanSpeed={ahu.fanSpeed} />
  },
  {
    key: 'power',
    header: 'Power, kW',
    width: '100px',
    align: 'center',
    render: (ahu) => formatNumber(ahu.power)
  },
  {
    key: 'consumption',
    header: 'Today Energy, kWh',
    width: '100px',
    align: 'center',
    render: (ahu) => formatNumber(ahu.consumption)
  },
  {
    key: 'actions',
    header: '',
    width: '30px',
    align: 'left',
    render: () => <img src={arrowRight} className="w-[4px] h-[7.5px]" />
  },
];

export function AHUTable({ tableData, isLoading, onRefresh }: AHUTable) {
  const [selectedAHU, setSelectedAHU] = useState<AHUData | null>(null);
  const [isVisibleMultiAHUModal, setIsVisibleMultiAHUModal] = useState(false)
  const [checked, setChecked] = useState<SelectedDeviceId[]>([])
  const [checkedAll, setCheckedAll] = useState(false)
  const checkAllRef = useRef<HTMLInputElement>(null);

  // Validate tableData to ensure it's a valid array using useMemo to avoid dependency issues
  const validTableData = useMemo(() => {
    try {
      return Array.isArray(tableData) ? tableData : [];
    } catch (err) {
      console.error('Error validating table data:', err);
      return [];
    }
  }, [tableData]);

  useEffect(() => {
    try {
      if (checkAllRef.current) {
        checkAllRef.current.indeterminate = checked.length > 0 && checked.length < (validTableData.length || 0);
      }
    } catch (err) {
      console.error('Error updating checkbox state:', err);
    }
  }, [checkAllRef, checked, validTableData]);

  // Map tableData to table data
  const handleCheckAll = (isChecked: boolean) => {
    try {
      if (isChecked) {
        setChecked(validTableData.map(v => v.id) as SelectedDeviceId[])
        setCheckedAll(true)
      } else {
        setChecked([])
        setCheckedAll(false)
      }
    } catch (err) {
      console.error('Error handling check all:', err);
      setChecked([])
      setCheckedAll(false)
    }
  }

  const handleCheck = (isChecked: boolean, ahu: AHUData) => {
    try {
      if (isChecked) {
        setChecked([
          ...checked,
          ahu.id,
        ])
        if (checked.length === (validTableData.length || 0) - 1) {
          setCheckedAll(true)
        }
      } else {
        setChecked(checked.filter(v => v !== ahu.id))
        setCheckedAll(false)
      }
    } catch (err) {
      console.error('Error handling check:', err);
      setChecked(checked.filter(v => v !== ahu.id))
      setCheckedAll(false)
    }
  }

  useEffect(() => {
    try {
      if (selectedAHU) {
        const updatedAHU = validTableData.find(v => v.id === selectedAHU.id) || null;
        setSelectedAHU(updatedAHU);
      }
    } catch (err) {
      console.error('Error updating selected AHU:', err);
    }
  }, [validTableData, selectedAHU])

  // Handle error states through UI rather than state

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-white-blue overflow-x-auto" style={{ width: '100%' }}>
        {isLoading ? (
          <div className="p-8">
            <div className="space-y-4">
              {/* Table header skeleton */}
              <div className="grid grid-cols-12 gap-4 h-12 bg-gray-50 rounded-lg items-center px-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="animate-skeleton h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
              
              {/* Table rows skeleton */}
              {Array.from({ length: 5 }).map((_, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-12 gap-4 h-16 border-b border-gray-100 items-center px-4" 
                     style={{ animationDelay: `${rowIndex * 100}ms` }}>
                  {Array.from({ length: 12 }).map((_, colIndex) => (
                    <div key={colIndex} className="animate-skeleton h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
              ))}
            </div>
            
            <div className="flex justify-center items-center mt-6 gap-4">
              <div className="flex gap-1 items-end">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-6 bg-primary rounded-sm wave-loader"
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600 font-medium">Loading AHU data...</p>
            </div>
          </div>
        ) : validTableData.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">No AHU data available</p>
            {onRefresh && (
              <Button onClick={onRefresh} className="bg-primary text-white hover:bg-primary/90">
                Refresh
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full table-auto text-[12px] font-inter" style={{ fontFamily: 'Inter, sans-serif !important', tableLayout: 'fixed' }}>
            <colgroup>
              {columns.map((column) => (
                <col key={column.key} style={{ width: column.width || 'auto' }} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-light-grey bg-light-grey h-[65px]">
                {/* Checkbox column is special case */}
                <th className="paragraph-10 p-4 text-dark-grey text-center">
                  <input type="checkbox" className="rounded border-gray-300"
                    checked={checkedAll}
                    ref={checkAllRef}
                    onChange={(e) => handleCheckAll(e.target.checked)}
                  />
                </th>

                {checked.length === 0 ? (
                  // Regular header columns
                  columns.slice(1).map((column) => (
                    <th key={column.key} className={cn(
                      "paragraph-11 p-2 text-dark-grey overflow-hidden text-ellipsis whitespace-normal",
                      column.align === 'center' && "text-center",
                      column.align === 'right' && "text-right"
                    )}>
                      {column.header}
                    </th>
                  ))
                ) : (
                  // Control mode header
                  <>
                    <th className="paragraph-12 px-2 text-dark-grey" colSpan={columns.length - 3}></th>
                    <th className="paragraph-10 pr-4 text-right" colSpan={2}>
                      <Button
                        size='sm'
                        className="bg-primary text-white h-[28px] ml-auto"
                        onClick={() => setIsVisibleMultiAHUModal(true)}
                      >
                        Control
                      </Button>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                tableData?.map((ahu, idx) => (
                  <tr
                    key={ahu.id}
                    className={cn(
                      "border-b border-light-grey hover:bg-bg/50 cursor-pointer h-[56.5px]",
                      idx % 2 === 1 && "bg-[#F9FAFF]",
                      ahu.event?.startsWith('CD-') && "bg-danger/5"
                    )}
                    onClick={() => setSelectedAHU(ahu)}
                  >
                    {/* Checkbox column is special case */}
                    <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="rounded border-gray-300"
                        checked={checked.some(item => item === ahu.id)}
                        onChange={(e) => handleCheck(e.target.checked, ahu)}
                      />
                    </td>

                    {/* Render all other columns */}
                    {columns.slice(1).map((column) => (
                      <td key={column.key} className={cn(
                        "p-2 overflow-hidden text-ellipsis whitespace-nowrap",
                        column.align === 'center' && "text-center",
                        column.align === 'right' && "text-right"
                      )}>
                        {column.render ? column.render(ahu) : null}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedAHU && (
        <AHUControlModal
          isOpen={true}
          onClose={() => setSelectedAHU(null)}
          data={{
            id: selectedAHU.id,
            name: selectedAHU.deviceName,
            autopilotId: selectedAHU.autopilotId,
            status: selectedAHU.status,
            setTemp: selectedAHU.setTemp || undefined,
            returnTemp: selectedAHU.returnTemp || undefined,
            mode: selectedAHU.mode,
            opMode: selectedAHU.opMode,
            fanSpeed: selectedAHU.fanSpeed,
            autopilotStatus: selectedAHU.autopilotStatus,
            user: selectedAHU?.user ?? '',
            lastUpdated: selectedAHU?.lastUpdated ?? '',
            isAlarm: selectedAHU.isAlarm,
            alarmCode: selectedAHU.isAlarm ? 'Unknown error' : '',
            isOnline: selectedAHU.isOnline,
          }}
        />
      )}
      {isVisibleMultiAHUModal && (
        <MultiAHUControlModal
          isOpen={true}
          onClose={() => setIsVisibleMultiAHUModal(false)}
          selected={checked.map(id => ({ id }))}
          options={(validTableData || []).map(device => ({
            key: device.id, // Add a key for React rendering
            value: device.id,
            label: device.deviceName,
            // Include additional device info for display
            deviceName: device.deviceName
          }))}
          onRefresh={onRefresh}
          data={{
            status: 'on',
            setTemp: 26,
            mode: 'cool',
            opMode: 'remote',
            fanSpeed: 'high'
          }}
        />
      )}
    </>
  );
}

